import axios from "axios";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { notificationActions } from "../../data/notification";
import useT from "../../i18n/useT";
import core from "../../core";

const PLACEHOLDERS = "{name} {surname} {vin} {mark} {model} {year} {status}";

const statusStyle = (s) => {
    if (s === "sent") return { background: "#34c38f", color: "#fff" };
    if (s === "failed") return { background: "#dc3545", color: "#fff" };
    if (s === "simulated") return { background: "#2f80ed", color: "#fff" };
    return { background: "#6c757d", color: "#fff" };
};

const Messaging = () => {
    const t = useT();
    const dispatch = useDispatch();
    const role = useSelector((state) => state.user.role);
    const isStaff = role === "admin" || role === "moderator";
    const isAdmin = role === "admin";

    const [tab, setTab] = useState("templates");
    const [templates, setTemplates] = useState([]);
    const [log, setLog] = useState([]);
    const [channelFilter, setChannelFilter] = useState("");
    const [updates, setUpdates] = useState(0);

    const notify = (text, type) => {
        dispatch(notificationActions.changeNotification({ show: true, text, type }));
        setTimeout(() => dispatch(notificationActions.changeNotification({ show: false, text: "", type: "" })), 4000);
    };

    useEffect(() => {
        if (!isStaff) return;
        axios.get("/api/message-templates").then((res) => setTemplates(res.data.data || [])).catch(() => {});
    }, [isStaff, updates]);

    useEffect(() => {
        if (!isStaff || tab !== "log") return;
        axios.get("/api/message-log" + (channelFilter ? `?channel=${channelFilter}` : "")).then((res) => setLog(res.data.data || [])).catch(() => {});
    }, [isStaff, tab, channelFilter, updates]);

    if (!isStaff) {
        return <div className="page-content-main-container"><h2 className="page-title">{t("msg.title")}</h2></div>;
    }

    const setField = (id, patch) => setTemplates((prev) => prev.map((x) => (x.id === id ? { ...x, ...patch } : x)));

    const saveTemplate = (tpl) => {
        axios.put(`/api/message-templates/${tpl.id}`, { subject: tpl.subject, body: tpl.body, enabled: tpl.enabled ? 1 : 0 })
            .then((res) => { notify(res.data.message.en, "success-notification"); setUpdates((u) => u + 1); })
            .catch((err) => notify(err.response?.data?.message?.en || "Error", "error-notification"));
    };

    const sendTest = (channel) => {
        axios.post("/api/message-templates/test", { channel })
            .then((res) => notify(`${channel}: ${res.data.data.status}`, "success-notification"))
            .catch((err) => notify(err.response?.data?.message?.en || "Error", "error-notification"));
    };

    return (
        <div className="page-content-main-container">
            <h2 className="page-title">{t("msg.title")}</h2>

            <div style={{ display: "flex", gap: "8px", marginBottom: "18px" }}>
                <button className={`btn btn-sm ${tab === "templates" ? "btn-primary" : "btn-outline-secondary"}`} onClick={() => setTab("templates")}>{t("msg.templates")}</button>
                <button className={`btn btn-sm ${tab === "log" ? "btn-primary" : "btn-outline-secondary"}`} onClick={() => setTab("log")}>{t("msg.log")}</button>
            </div>

            {tab === "templates" && (
                <>
                    <div style={{ fontSize: "12px", opacity: 0.7, marginBottom: "14px" }}>{t("msg.placeholders")}: <code>{PLACEHOLDERS}</code></div>
                    {templates.map((tpl) => (
                        <div key={tpl.id} style={{ background: "var(--card)", border: "1px solid var(--border)", borderRadius: "12px", padding: "16px", marginBottom: "12px", maxWidth: "720px" }}>
                            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "10px" }}>
                                <strong style={{ fontSize: "14px" }}>{t(`msg.event_${tpl.event_key}`) || tpl.event_key} · {t(`msg.${tpl.channel}`)}</strong>
                                <label style={{ display: "flex", alignItems: "center", gap: "6px", fontSize: "13px" }}>
                                    <input type="checkbox" checked={!!tpl.enabled} disabled={!isAdmin} onChange={(e) => setField(tpl.id, { enabled: e.target.checked ? 1 : 0 })} />
                                    {t("msg.enabled")}
                                </label>
                            </div>
                            {tpl.channel === "email" && (
                                <div style={{ marginBottom: "8px" }}>
                                    <label style={{ fontSize: "12px", opacity: 0.7 }}>{t("msg.subject")}</label>
                                    <input className="form-control" value={tpl.subject || ""} disabled={!isAdmin} onChange={(e) => setField(tpl.id, { subject: e.target.value })} />
                                </div>
                            )}
                            <label style={{ fontSize: "12px", opacity: 0.7 }}>{t("msg.body")}</label>
                            <textarea className="form-control" rows={tpl.channel === "email" ? 4 : 2} value={tpl.body || ""} disabled={!isAdmin} onChange={(e) => setField(tpl.id, { body: e.target.value })} />
                            {isAdmin && (
                                <div style={{ marginTop: "10px", display: "flex", gap: "8px", flexWrap: "wrap" }}>
                                    <button className="btn btn-sm btn-primary" onClick={() => saveTemplate(tpl)}>{t("msg.save")}</button>
                                    {tpl.channel === "sms" && <button className="btn btn-sm btn-outline-secondary" title={t("msg.testHint")} onClick={() => sendTest("sms")}>{t("msg.testSms")}</button>}
                                    {tpl.channel === "email" && <button className="btn btn-sm btn-outline-secondary" title={t("msg.testHint")} onClick={() => sendTest("email")}>{t("msg.testEmail")}</button>}
                                </div>
                            )}
                        </div>
                    ))}
                </>
            )}

            {tab === "log" && (
                <>
                    <div style={{ marginBottom: "12px" }}>
                        <select className="form-control" style={{ width: "auto" }} value={channelFilter} onChange={(e) => setChannelFilter(e.target.value)}>
                            <option value="">{t("msg.allChannels")}</option>
                            <option value="sms">{t("msg.sms")}</option>
                            <option value="email">{t("msg.email")}</option>
                            <option value="inapp">{t("msg.inapp")}</option>
                        </select>
                    </div>
                    {log.length === 0 ? (
                        <div style={{ opacity: 0.6 }}>{t("msg.none")}</div>
                    ) : (
                        <div style={{ overflowX: "auto" }}>
                            <table className="table" style={{ width: "100%" }}>
                                <thead>
                                    <tr>
                                        <th>{t("msg.channel")}</th>
                                        <th>{t("msg.recipient")}</th>
                                        <th>{t("msg.body")}</th>
                                        <th>{t("msg.status")}</th>
                                        <th>{t("msg.date")}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {log.map((m) => (
                                        <tr key={m.id}>
                                            <td>{t(`msg.${m.channel}`) || m.channel}</td>
                                            <td style={{ fontSize: "12px" }}>{m.recipient || "—"}</td>
                                            <td style={{ fontSize: "12px", maxWidth: "320px" }}>{m.subject ? <strong>{m.subject}: </strong> : null}{(m.body || "").slice(0, 120)}</td>
                                            <td><span style={{ ...statusStyle(m.status), padding: "2px 10px", borderRadius: "12px", fontSize: "12px" }}>{t(`msg.${m.status}`) || m.status}</span></td>
                                            <td style={{ fontSize: "12px" }}>{m.created_at ? core.formdatDate(m.created_at, "dd-mm-yyyy") : "—"}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </>
            )}
        </div>
    );
};

export default Messaging;
